import React from "react"
import PropTypes from "prop-types"
import {
  Box,
  GridItem,
  Grid,
  Breadcrumb,
  Link,
  HeadingTwo,
  TextXS,
  TextSM,
  Text,
  Divider,
  LinkExternalIcon,
  Flex,
  Button,
  Image,
} from "datastory-shared/ui"
import { useTranslation } from "datastory-shared/utils/i18n"

import { ShareArticle } from "components/blocks"

const TopicItem = ({ topic }) => (
  <Button
    variant={"outline"}
    as={Link}
    href={`/services/glossary/${topic.entity_meta.local_id}`}
  >
    {topic.entity_meta.name[0]?.i18n}
  </Button>
)

TopicItem.propTypes = {
  topic: PropTypes.object,
}

export const SinglePage = ({
  glossaryItem: {
    og_name,
    og_illustration,
    og_definition,
    og_glossaryExampleText,
    og_datastoryGlossaryCategory,
    og_externalUrl,
  },
}) => {
  const name = og_name[0]?.i18n
  const definition = og_definition[0]?.i18n
  const image = og_illustration[0]
  const example = og_glossaryExampleText[0]?.i18n
  const externalUrl = og_externalUrl[0]?.postgres_varchar

  const { t } = useTranslation()

  return (
    <Box my="lg">
      <Grid justifyContent="center">
        <GridItem colStart={[0, 0, 0, 2, 2]} colSpan={[4, 6, 6, 12, 12]}>
          <Breadcrumb
            data-cy="breadcrumb"
            items={[
              {
                href: "/services/glossary",
                label: t("og_page--6a7c04a9:title"),
              },
              {
                href: `/services/glossary/${og_datastoryGlossaryCategory[0].entity_meta.local_id}`,
                label:
                  og_datastoryGlossaryCategory[0].entity_meta.name[0]?.i18n,
              },
              { label: name },
            ]}
          />
        </GridItem>

        <GridItem colStart={[1, 1, 2, 2, 2]} colSpan={[4, 6, 4, 10, 10]}>
          {image?.entity_meta?.local_id && (
            <Flex justifyContent="center" mt="lg" maxHeight="60vh">
              <Image
                width={620}
                height={600}
                src={image.entity_meta.local_id}
                alt={`Image portraying ${name}`}
              />
            </Flex>
          )}
        </GridItem>
      </Grid>

      <Grid mt="lg">
        <GridItem colStart={[0, 0, 0, 2, 2]} colSpan={[4, 6, 6, 6, 6]}>
          <HeadingTwo data-cy="title" mb="md">
            {name}
          </HeadingTwo>
          <Text data-cy="definition">{definition}</Text>
          {example && (
            <Box mt="lg">
              <TextXS muted>Example of use</TextXS>
              <Text mt="md">{example}</Text>
            </Box>
          )}
        </GridItem>
        <GridItem colStart={[0, 0, 0, 9, 9]} colSpan={[4, 4, 6, 3, 3]}>
          <Box data-cy="topics" mt="lg" mb="lg">
            <TextXS as="div" mb="md" muted>
              TOPICS
            </TextXS>
            {og_datastoryGlossaryCategory.map(i => (
              <TopicItem key={i.id} topic={i} />
            ))}
          </Box>
          {externalUrl && (
            <Box>
              <TextXS muted>EXTERNAL LINK</TextXS>
              <Flex mt="md" alignItems="center">
                <LinkExternalIcon mr="sm" />
                <Link href={externalUrl} muted textDecoration="none">
                  <TextSM fontWeight={600} muted>
                    {externalUrl}
                  </TextSM>
                </Link>
              </Flex>
            </Box>
          )}
        </GridItem>
      </Grid>

      <Grid>
        <GridItem colStart={[1, 1, 2, 4, 4]} colSpan={[4, 6, 4, 6, 6]} mt="xl">
          <Divider />
          <Box py="lg">
            <ShareArticle />
          </Box>
          <Divider />
        </GridItem>
      </Grid>
    </Box>
  )
}

SinglePage.propTypes = {
  glossaryItem: PropTypes.shape({
    name: PropTypes.array,
    illustration: PropTypes.array,
    definition: PropTypes.array,
    glossaryExampleText: PropTypes.array,
    datastoryGlossaryCategory: PropTypes.array,
    externalUrl: PropTypes.array,
  }),
}
